import { useEffect, useState } from 'react';
import {
    BlockingLoader,
    CreationNote,
    Note,
    NoteScope,
    Pet,
    Policy,
    useClinicalHistory,
    useNotes,
    usePet,
} from '@barkibu/noma-commons';
import PolicyIncidentsDetails from './PolicyIncidentsDetails';
import { usePolicyIncidents } from './usePolicyIncidents';

export type PolicyIncidentsPageProps = {
    policyIdentifier: string;
};

export type PolicyIncidentsPageContentProps = {
    pet: Pet;
    policyIdentifier: string;
};

const PolicyIncidentsPage = ({ policyIdentifier }: PolicyIncidentsPageProps) => {
    const { pet } = usePet(policyIdentifier);

    if (!pet) {
        return <BlockingLoader />;
    }

    return <PolicyIncidentsPageContent pet={pet} policyIdentifier={policyIdentifier} />;
};

const PolicyIncidentsPageContent = ({ pet, policyIdentifier }: PolicyIncidentsPageContentProps) => {
    const { requestSummaries, removeDocument, updateDocument, markClaimRequestAsManuallyClosed } = usePolicyIncidents(
        pet.policies.map((policy) => policy.identifier)
    );
    const activePolicy =
        pet.policies.find((policy: Policy) => policy.identifier == policyIdentifier) ?? pet.policies[0];
    const activePolicyIdentifier = activePolicy.identifier;
    const getRequestSummariesWithDocuments = requestSummaries
        ? requestSummaries.filter((it) => it.supportingDocuments?.length)
        : [];
    const [requestSummariesState, setRequestSummariesState] = useState(getRequestSummariesWithDocuments);
    const { clinicalHistory } = useClinicalHistory(activePolicyIdentifier);
    const policyRequestSummary = requestSummaries?.find(
        (summary) => summary.policyIdentifier == activePolicyIdentifier
    );
    const claimRequestKey = policyRequestSummary?.claimRequestKey;

    const {
        notes: policyNotes,
        createNote: addPolicyNote,
        deleteNote: deletePolicyNote,
    } = useNotes(NoteScope.POLICY, claimRequestKey, activePolicyIdentifier);
    const {
        notes: petParentNotes,
        createNote: addPetParentNote,
        deleteNote: deletePetParentNote,
    } = useNotes(NoteScope.PET_PARENT, claimRequestKey, activePolicyIdentifier);
    const {
        notes: petNotes,
        createNote: addPetNote,
        deleteNote: deletePetNote,
    } = useNotes(NoteScope.PET, claimRequestKey, activePolicyIdentifier);

    useEffect(() => {
        if (requestSummaries) {
            setRequestSummariesState(getRequestSummariesWithDocuments);
        }
    }, [requestSummaries]);

    const handleAddNote = (note: CreationNote) => {
        switch (note.scope) {
            case NoteScope.POLICY:
                addPolicyNote(note);
                break;
            case NoteScope.PET:
                addPetNote(note);
                break;
            case NoteScope.PET_PARENT:
                addPetParentNote(note);
                break;
        }
    };

    const handleDeleteNote = (note: Note) => {
        switch (note.scope) {
            case NoteScope.POLICY:
                deletePolicyNote(note.key);
                break;
            case NoteScope.PET:
                deletePetNote(note.key);
                break;
            case NoteScope.PET_PARENT:
                deletePetParentNote(note.key);
                break;
        }
    };

    const handleCloseClaimRequest = async (claimRequestKey: string) => {
        if (claimRequestKey) {
            await markClaimRequestAsManuallyClosed(claimRequestKey);
            setRequestSummariesState(requestSummariesState.filter((el) => el.claimRequestKey != claimRequestKey));
        }
    };

    if (requestSummariesState != undefined) {
        return (
            <PolicyIncidentsDetails
                requestSummaries={requestSummariesState}
                onGoBack={() => history.back()}
                onRemoveDocument={removeDocument}
                onUpdateDocument={updateDocument}
                onCloseClaimRequest={handleCloseClaimRequest}
                clinicalHistory={clinicalHistory}
                policies={pet.policies}
                policyNotes={policyNotes}
                petNotes={petNotes}
                petParentNotes={petParentNotes}
                onAddNote={handleAddNote}
                onDeleteNote={handleDeleteNote}
            />
        );
    } else {
        return <BlockingLoader />;
    }
};

export default PolicyIncidentsPage;
